import { cssManager, css, type CSSResult } from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';

export const tableStyles: CSSResult[] = [
  themeDefaultStyles,
  cssManager.defaultStyles,
  css`
    :host {
      display: block; width: 100%; min-width: 0;
      --table-action-size: 28px;
      --table-header-height: 36px;
      --dees-table-flash-color: var(--dees-color-accent-warning);
      --dees-table-flash-easing: cubic-bezier(0.22, 0.61, 0.36, 1);
    }
    /* Keep the viewport-fixed header outside layout containment. */
    .tableContainer { container: dees-table / inline-size; }
    dees-tile, .floatingHeader { color: var(--dees-color-text-primary); font: var(--dees-font-control-size)/1.4 var(--dees-font-family); cursor: default; }
    .header { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 16px; padding: 18px 20px; }
    .headingContainer { min-width: 0; flex: 1 1 180px; }
    .titleRow { display: flex; align-items: baseline; gap: 8px; }
    .heading1 { margin: 0; font-size: 16px; font-weight: 600; letter-spacing: -.02em; line-height: 1.3; }
    .rowCount { font-size: 13px; color: var(--dees-color-text-muted); font-variant-numeric: tabular-nums; }
    .heading2 { margin: 5px 0 0; font-size: 12px; color: var(--dees-color-text-secondary); line-height: 1.4; }
    .toolbar { display: flex; align-items: center; gap: 10px; min-width: 0; flex-wrap: wrap; }
    .headerActions, .footerActions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
    .headerActions:empty { display: none; }
    button { font: inherit; }
    .control {
      box-sizing: border-box; display: inline-flex; flex: 0 0 auto; justify-content: center; align-items: center; gap: 6px;
      border: 0; border-radius: var(--dees-radius-md); background: transparent; color: var(--dees-color-text-secondary);
      cursor: pointer; line-height: 1; transition: background-color var(--dees-transition-fast), color var(--dees-transition-fast);
    }
    @media (hover: hover) {
      .control:hover { color: var(--dees-color-text-primary); background: var(--dees-color-hover); }
      tbody tr:hover { --table-row-surface: var(--dees-color-row-hover); }
      .sortControl:hover { color: var(--dees-color-text-primary); }
    }
    .control:active { background: var(--dees-color-active); }
    button:focus-visible, input:focus-visible, :host(:focus-visible) { outline: 2px solid var(--dees-color-focus-ring); outline-offset: 2px; }
    .headerAction {
      min-height: 32px; padding: 0 11px; border: 1px solid var(--dees-color-border-default);
      background: var(--dees-color-control-bg); color: var(--dees-color-text-primary);
      font-size: 12px; font-weight: 500; white-space: nowrap;
    }
    .searchField {
      display: flex; align-items: center; gap: 8px; width: 240px; min-width: 0; min-height: 32px;
      box-sizing: border-box; padding-left: 10px; border: 1px solid var(--dees-color-border-default);
      border-radius: var(--dees-radius-md); background: var(--dees-color-control-bg); color: var(--dees-color-text-muted);
    }
    .searchField:focus-within { border-color: var(--dees-color-focus-ring); box-shadow: 0 0 0 1px var(--dees-color-focus-ring); }
    .searchField > dees-icon { flex: 0 0 auto; }
    .searchInput { box-sizing: border-box; width: 100%; min-width: 0; padding: 6px 0; border: 0; outline: none; background: transparent; color: var(--dees-color-text-primary); font: inherit; font-size: 12px; }
    .searchInput:focus-visible { outline: none; }
    .searchInput::placeholder { color: var(--dees-color-text-muted); }
    .searchOptions { width: 30px; align-self: stretch; border-radius: 0 var(--dees-radius-lg) var(--dees-radius-lg) 0; }
    .searchOptions:focus-visible { outline-offset: -3px; }

    .tableScroll { position: relative; overflow-x: auto; overflow-y: visible; scrollbar-width: thin; }
    :host([fixed-height]) .tableScroll { max-height: var(--table-max-height, 360px); overflow: auto; scrollbar-gutter: stable; }
    .floatingHeader { position: fixed; top: 0; left: 0; z-index: 100; visibility: hidden; overflow: hidden; pointer-events: none; }
    .floatingHeader.active { visibility: visible; }
    .floatingHeader table { margin: 0; }
    .floatingHeader th { pointer-events: auto; }
    table { width: max-content; min-width: 100%; caption-side: bottom; font-size: inherit; border-collapse: separate; border-spacing: 0; }
    th, td { box-sizing: border-box; padding: 6px 16px; text-align: start; vertical-align: middle; }
    th { height: var(--table-header-height); background: var(--dees-color-bg-primary); color: var(--dees-color-text-secondary); font-size: 12px; font-weight: 500; border-bottom: 1px solid var(--dees-color-border-subtle); }
    td { height: 40px; position: relative; color: var(--dees-color-text-primary); border-bottom: 1px solid var(--dees-color-border-subtle); font-size: 13px; }
    th:first-child, td:first-child { padding-inline-start: 20px; }
    th:last-child, td:last-child { padding-inline-end: 20px; }
    tbody tr:last-child td { border-bottom: 0; }
    tbody tr { --table-row-surface: var(--dees-color-bg-primary); background: var(--table-row-surface); user-select: none; }
    tbody tr.selected { --table-row-surface: color-mix(in srgb, var(--dees-color-accent-primary) 13%, var(--dees-color-bg-primary)); }
    tbody tr.hasAttachment { --table-row-surface: color-mix(in srgb, var(--dees-color-text-success) 12%, var(--dees-color-bg-primary)); }
    :host([show-grid]) th, :host([show-grid]) td,
    :host([show-vertical-lines]) th, :host([show-vertical-lines]) td { border-inline-end: 1px solid var(--dees-color-border-default); }
    :host([show-grid]) td, :host([show-horizontal-lines]) td { border-bottom: 1px solid var(--dees-color-border-default); }
    :host([show-grid]) th:last-child, :host([show-grid]) td:last-child,
    :host([show-vertical-lines]) th:last-child, :host([show-vertical-lines]) td:last-child { border-inline-end: 0; }
    :host([show-grid]) tbody tr:last-child td { border-bottom: 0; }
    [data-align='end'] { text-align: end; font-variant-numeric: tabular-nums; }
    [data-align='center'] { text-align: center; }
    .sortControl { display: flex; align-items: center; gap: 5px; width: 100%; min-height: 24px; border: 0; padding: 0; text-align: inherit; color: inherit; background: transparent; font: inherit; cursor: pointer; white-space: nowrap; }
    th[aria-sort='ascending'], th[aria-sort='descending'] { color: var(--dees-color-text-primary); }
    [data-align='end'] .sortControl { justify-content: flex-end; }
    [data-align='center'] .sortControl { justify-content: center; }
    .sortArrow { font-size: 10px; opacity: .75; }
    .sortBadge { padding: 2px 4px; border-radius: var(--dees-radius-sm); font-size: 10px; color: var(--dees-color-accent-primary); background: color-mix(in srgb, var(--dees-color-accent-primary) 12%, transparent); }
    :host([fixed-height]) thead th { position: sticky; top: 0; z-index: 2; }
    :host([fixed-height]) thead tr.filtersRow th { top: var(--table-header-height); }
    .selectionCol { width: 44px; padding: 0 8px !important; text-align: center; }
    .selectionCol dees-input-checkbox { display: inline-block; vertical-align: middle; }
    .selectionCol dees-input-checkbox::part(control) { align-items: center; justify-content: center; }
    .actionsCol { position: sticky; right: 0; width: calc(var(--table-inline-action-count, 1) * (var(--table-action-size) + 4px) + 32px); white-space: nowrap; }
    thead th.actionsCol, :host([fixed-height]) thead th.actionsCol { z-index: 3; }
    tbody td.actionsCol {
      z-index: 1;
      background: linear-gradient(var(--table-row-surface), var(--table-row-surface)) var(--dees-color-bg-primary);
    }
    /* One continuous, non-interactive edge, only while data passes beneath the pinned column. */
    :host([data-actions-pinned]) .actionsCol::before {
      content: ''; position: absolute; inset-block: 0; right: 100%; width: 8px; pointer-events: none;
      background: linear-gradient(to left, var(--dees-color-scroll-edge-shadow), transparent);
      border-right: 1px solid var(--dees-color-border-subtle);
    }
    .actionsHeading { display: block; text-align: end; }
    .actionsContainer { display: flex; align-items: center; justify-content: flex-end; gap: 4px; min-height: var(--table-action-size); }
    .action { min-width: var(--table-action-size); height: var(--table-action-size); padding: 0 5px; font-size: 12px; }
    .action dees-icon { flex: 0 0 auto; }
    .compactRowActions { display: none; }
    /* The viewport-fixed clone sits outside the query container. Its compact
       state mirrors that container, even when an ancestor clips it narrower. */
    .floatingHeader.compact .actionsHeading { font-size: 0; }
    .floatingHeader.compact .actionsHeading::after { content: '•••' / ''; font-size: 12px; letter-spacing: 1px; }
    .floatingHeader.compact th.actionsCol { width: calc(var(--table-action-size) + 16px); padding-inline: 8px; }
    .innerCellContainer { position: relative; min-height: 24px; line-height: 24px; }
    .spacer td { height: inherit; padding: 0; border: 0; }
    td.editable { cursor: text; }
    /* Integrated editors delegate focus chrome to the cell. Other editor
       types retain their own control ring, never a second cell outline. */
    td.focused:not(.editingCell), td.editingCell:has(dees-input-text, dees-input-dropdown) {
      outline: 2px solid var(--dees-color-focus-ring); outline-offset: -2px;
    }
    td.editingCell { padding: 0; }
    td.editingCell .innerCellContainer { padding: 0; line-height: normal; }
    td.editingCell dees-input-text, td.editingCell dees-input-checkbox,
    td.editingCell dees-input-dropdown, td.editingCell dees-input-datepicker,
    td.editingCell dees-input-tags { display: block; width: 100%; }
    .filtersRow th { padding: 6px 12px 10px; }
    .filtersRow input { width: 100%; box-sizing: border-box; min-height: 28px; padding: 4px 8px; border: 1px solid var(--dees-color-border-default); border-radius: var(--dees-radius-md); background: var(--dees-color-control-bg); color: var(--dees-color-text-primary); font: inherit; }
    .noDataSet { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 40px 20px; text-align: center; color: var(--dees-color-text-secondary); font-size: 13px; }
    .noDataSet > dees-icon { margin-bottom: 4px; color: var(--dees-color-text-muted); }
    .noDataSet strong { color: var(--dees-color-text-primary); font-size: 14px; font-weight: 600; }
    .noDataSet .control { margin-top: 8px; }
    .emptyRow td { padding: 0; }
    .footer { box-sizing: border-box; display: flex; align-items: center; justify-content: space-between; gap: 8px 16px; flex-wrap: wrap; min-height: 40px; padding: 6px 20px; width: 100%; color: var(--dees-color-text-muted); font-size: 12px; }
    .tableStatistics { font-variant-numeric: tabular-nums; }
    .statisticsDivider { padding: 0 8px; }
    .footerAction { min-height: 28px; padding: 0 8px; font-size: 12px; }

    /* Motion is limited to cells whose values actually changed. */
    .innerCellContainer.flashing { animation: dees-table-cell-flash var(--dees-table-flash-duration, 900ms) var(--dees-table-flash-easing); }
    @keyframes dees-table-cell-flash {
      0%, 35% { color: var(--dees-table-flash-color); }
      100% { color: var(--dees-color-text-primary); }
    }
    .innerCellContainer.flashing-border { animation: dees-table-cell-flash-border var(--dees-table-flash-duration, 900ms) var(--dees-table-flash-easing); border-radius: 3px; }
    @keyframes dees-table-cell-flash-border {
      0%, 35% { box-shadow: inset 0 0 0 1.5px var(--dees-table-flash-color); background: color-mix(in srgb, var(--dees-color-accent-warning) 10%, transparent); }
      100% { box-shadow: inset 0 0 0 1.5px transparent; background: transparent; }
    }
    .flashConfigWarning { display: flex; align-items: center; gap: 8px; margin: 12px 20px; padding: 10px 12px; border-left: 3px solid var(--dees-color-accent-warning); border-radius: var(--dees-radius-sm); background: color-mix(in srgb, var(--dees-color-accent-warning) 18%, transparent); color: var(--dees-color-badge-warning-fg); font-size: 12px; }
    .flashConfigWarning code { font-family: var(--dees-font-family-mono); }
    @media (prefers-reduced-motion: reduce) {
      .control { transition: none; }
      .innerCellContainer.flashing { animation: none; color: var(--dees-table-flash-color); }
      .innerCellContainer.flashing-border { animation: none; box-shadow: inset 0 0 0 1.5px var(--dees-table-flash-color); background: color-mix(in srgb, var(--dees-color-accent-warning) 10%, transparent); }
    }
    @container dees-table (max-width: 700px) {
      .headingContainer { flex-basis: 100%; }
      .toolbar { width: 100%; }
      .searchField { flex: 1 1 180px; }
      .primaryRowAction { display: none; }
      .compactRowActions { display: inline-flex; }
      .actionsHeading { font-size: 0; }
      .actionsHeading::after { content: '•••' / ''; font-size: 12px; letter-spacing: 1px; }
      th.actionsCol, td.actionsCol { width: calc(var(--table-action-size) + 16px); padding-inline: 8px; }
    }
    @container dees-table (max-width: 440px) {
      .header { padding: 16px; gap: 12px; }
      .toolbar { gap: 8px; }
      .searchField { flex-basis: 100%; width: 100%; }
      .heading1 { font-size: 15px; }
      .footer { padding-inline: 16px; }
    }
    @media (pointer: coarse) {
      :host { --table-action-size: 44px; --table-header-height: 48px; }
      .control, .sortControl, .filtersRow input { min-height: 44px; }
      .searchField { min-height: 44px; }
      .searchOptions { width: 44px; }
      .searchInput { min-height: 42px; font-size: 16px; }
      thead tr:first-child th { padding-block: 2px; }
      td { height: 48px; padding-block: 2px; }
      .selectionCol { min-width: 52px; padding-inline: 4px !important; }
      .selectionCol dees-input-checkbox::part(control) { min-width: 44px; min-height: 44px; }
    }
  `,
];
